/**
 * Застаріла збірка після деплою.
 *
 * Куплено 28.09.2026: вкладка тримала стару версію, новий деплой уже прибрав
 * її частини (`OrderDetail-<хеш>.js`), і перехід на будь-який екран давав
 * «Цей екран зламався». Людина бачить поломку там, де просто вийшло оновлення.
 *
 * Лікування — один раз перезавантажитись начисто (без старого service worker
 * і кешу). Але ЛИШЕ коли справді вийшла нова версія: той самий збій імпорту
 * дає і відсутня мережа, і тоді чистка кешу вбила б офлайн-роботу планшета
 * (код-ревю 29.09.2026). Запобіжник від циклу: не частіше, ніж раз на 30 с.
 */
const KEY = "mes:stale-reload-at";

export function isStaleChunkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk|reading 'default'/i.test(msg);
}

/** Чи на сервері справді інша збірка. Немає мережі — ні (кеш не чіпаємо). */
async function newBuildOnServer(): Promise<boolean> {
  if (!navigator.onLine) return false;
  try {
    const r = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!r.ok) return false;
    const d = (await r.json()) as { build?: string };
    return !!d.build && d.build !== __BUILD_ID__;
  } catch {
    return false;
  }
}

/** Одна чистка на сторінку: два виклики (preloadError і межа помилок)
 *  отримують той самий результат, а не «перший чистить, другий — зламалось». */
let pending: Promise<boolean> | null = null;

/**
 * `true` — пішли перезавантажуватись. `false` — не вийшла нова версія (або
 * щойно вже пробували): тоді показуємо звичайний екран помилки.
 * `force` — людина сама натиснула «Оновити»: без запобіжника й перевірки.
 */
export function reloadToNewVersion(force = false): Promise<boolean> {
  if (pending) return pending;
  pending = (async () => {
    if (!force) {
      try {
        const last = Number(sessionStorage.getItem(KEY) || 0);
        if (Date.now() - last < 30_000) return false;
      } catch {
        /* сховище недоступне — без запобіжника */
      }
      // Без мережі не чіпаємо нічого: це не оновлення, а обрив звʼязку.
      if (!navigator.onLine) return false;
      try {
        sessionStorage.setItem(KEY, String(Date.now()));
      } catch {
        /* не критично */
      }
      // Та сама збірка на сервері — файл просто ще не розійшовся по CDN
      // одразу після деплою (29.09.2026: 404 на мить, потім 200). Кеш не
      // чистимо, лише перезавантажуємось трохи згодом.
      if (!(await newBuildOnServer())) {
        await new Promise((r) => setTimeout(r, 1500));
        window.location.reload();
        return true;
      }
    }
    try {
      const regs = (await navigator.serviceWorker?.getRegistrations()) ?? [];
      await Promise.all(regs.map((r) => r.unregister()));
      const keys = (await caches?.keys()) ?? [];
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch {
      /* навіть без чистки перезавантаження зазвичай рятує */
    }
    window.location.reload();
    return true;
  })().finally(() => {
    // Перезавантаження не сталось (false) — наступна спроба має бути можлива.
    setTimeout(() => {
      pending = null;
    }, 0);
  });
  return pending;
}
